import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';

export const EquipmentPage = () => {
  const equipment = [
    {
      category: 'Protection',
      items: [
        { name: 'Combinaison d\'apiculteur', price: '60-150€', description: 'Protection complète du corps', essential: 'essentiel' },
        { name: 'Gants en cuir', price: '15-30€', description: 'Protection des mains contre les piqûres', essential: 'essentiel' },
        { name: 'Voile ou chapeau', price: '20-40€', description: 'Protection du visage et de la tête', essential: 'essentiel' },
      ]
    },
    {
      category: 'Manipulation',
      items: [
        { name: 'Enfumoir', price: '25-50€', description: 'Calme les abeilles lors des visites', essential: 'essentiel' },
        { name: 'Lève-cadre', price: '5-15€', description: 'Outil indispensable pour manipuler les cadres', essential: 'essentiel' },
        { name: 'Brosse à abeilles', price: '8-15€', description: 'Déplacer délicatement les abeilles', essential: 'recommande' },
      ]
    },
    {
      category: 'Ruche et structure',
      items: [
        { name: 'Ruche complète (Dadant)', price: '120-200€', description: 'Corps, hausse, cadres, toit', essential: 'essentiel' },
        { name: 'Cadres supplémentaires', price: '2-4€/unité', description: 'Pour l\'extension de la ruche', essential: 'recommande' },
        { name: 'Support de ruche', price: '30-60€', description: 'Surélévation et stabilité', essential: 'essentiel' },
      ]
    },
    {
      category: 'Récolte',
      items: [
        { name: 'Extracteur manuel', price: '150-300€', description: 'Extraction du miel des cadres', essential: 'recommande' },
        { name: 'Maturateur', price: '80-150€', description: 'Décantation et filtration du miel', essential: 'recommande' },
        { name: 'Couteau à désoperculer', price: '15-30€', description: 'Retirer les opercules des cadres', essential: 'recommande' },
        { name: 'Pots en verre', price: '0.50-1€/unité', description: 'Conservation du miel récolté', essential: 'recommande' },
      ]
    },
    {
      category: 'Entretien',
      items: [
        { name: 'Nourrisseur', price: '10-25€', description: 'Contenant pour distribuer le sirop aux abeilles (achat unique)', essential: 'recommande' },
        { name: 'Grille à reine', price: '8-15€', description: 'Séparer corps et hausse', essential: 'recommande' },
      ]
    },
    {
      category: 'Consommables annuels',
      items: [
        { name: 'Traitement anti-varroa', price: '20-40€', description: 'Lutte contre le parasite varroa (obligatoire chaque année)', essential: 'recurrent' },
        { name: 'Sirop de nourrissement', price: '10-20€', description: 'Sucre ou sirop pour nourrir les abeilles en automne/hiver', essential: 'recurrent' },
        { name: 'Combustible enfumoir', price: '5-10€', description: 'Carton, lavande séchée, copeaux de bois (à renouveler)', essential: 'recurrent' },
        { name: 'Cire gaufrée', price: '10-15€', description: 'Renouvellement des vieux cadres (2-3 cadres par an)', essential: 'recurrent' },
        { name: 'Pots et étiquettes', price: '5-15€', description: 'Pour conditionner et étiqueter le miel récolté', essential: 'recurrent' },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SEO 
        title="Équipement Apiculture Urbaine | Matériel & Budget pour Ruche de Balcon - UrbanHive"
        description="Liste complète de l'équipement nécessaire pour l'apiculture urbaine : ruche, protection, outils d'entretien et consommables. Budget détaillé installation + coûts annuels de 300-500€."
        keywords="équipement apiculture urbaine, matériel ruche balcon, budget apiculture ville, ruche équipement, protection apiculteur, outils apiculture"
        url="/equipment"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section className="relative py-24 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1758522964891-01b41d0c2224" 
              alt="Équipement d'apiculture"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
                Équipement de l'Apiculteur
              </h1>
              <p className="text-lg text-white/90">
                Liste complète du matériel nécessaire pour démarrer l'apiculture urbaine.
              </p>
            </div>
          </div>
        </section>

        {/* Tableau comparatif */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              
              {/* Header du tableau avec les 3 catégories */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
                <div className="lg:col-span-1"></div>
                
                {/* Colonne Essentiel */}
                <div className="rounded-xl p-6 text-center shadow-xl" style={{ background: 'linear-gradient(to bottom right, #8DA399, #7a9188)' }}>
                  <div className="text-white">
                    <div className="text-2xl font-bold mb-2">Essentiel</div>
                    <p className="text-sm text-white/90">Indispensable pour démarrer</p>
                    <div className="text-3xl font-bold mt-4">300-400€</div>
                  </div>
                </div>
                
                {/* Colonne Recommandé */}
                <div className="rounded-xl p-6 text-center shadow-xl" style={{ background: 'linear-gradient(to bottom right, #F6C90E, #d4ab0a)' }}>
                  <div className="text-white">
                    <div className="text-2xl font-bold mb-2">Recommandé</div>
                    <p className="text-sm text-white/90">Pour une pratique confortable</p>
                    <div className="text-3xl font-bold mt-4">+200-300€</div>
                  </div>
                </div>
                
                {/* Colonne Récurrent */}
                <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-xl p-6 text-center shadow-xl">
                  <div className="text-white">
                    <div className="text-2xl font-bold mb-2">Récurrent</div>
                    <p className="text-sm text-white/90">Coûts annuels</p>
                    <div className="text-3xl font-bold mt-4">50-100€/an</div>
                  </div>
                </div>
              </div>

              {/* Tableau des équipements */}
              <Card className="border-2 border-gray-200 overflow-hidden">
                <CardContent className="p-0">
                  {equipment.map((category, catIndex) => (
                    <div key={catIndex} className={catIndex > 0 ? 'border-t-2 border-gray-200' : ''}>
                      {/* Titre de la catégorie */}
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-serif font-bold text-gray-900">{category.category}</h2>
                      </div>
                      
                      {/* Items de la catégorie */}
                      {category.items.map((item, itemIndex) => (
                        <div 
                          key={itemIndex} 
                          className="equipment-row grid grid-cols-1 lg:grid-cols-4 gap-4 px-6 py-4 hover:shadow-md transition-all duration-200 border-b border-gray-100 last:border-b-0 cursor-pointer"
                        >
                          {/* Nom et description */}
                          <div className="lg:col-span-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                            <div className="text-sm font-bold text-honey">{item.price}</div>
                          </div>
                          
                          {/* Essentiel */}
                          <div className="flex items-center justify-center">
                            {item.essential === 'essentiel' && (
                              <svg className="h-8 w-8 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Recommandé */}
                          <div className="flex items-center justify-center">
                            {item.essential === 'recommande' && (
                              <svg className="h-8 w-8 text-honey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Récurrent */}
                          <div className="flex items-center justify-center">
                            {item.essential === 'recurrent' && (
                              <svg className="h-8 w-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Légende */}
              <div className="mt-8 p-6 bg-cream rounded-lg border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">💡</div>
                  <div>
                    <p className="text-sm text-gray-700">
                      <strong>Comment lire ce tableau :</strong> Chaque équipement est classé dans une ou plusieurs catégories. 
                      Un ✓ vert indique que l'équipement est essentiel au démarrage, un ✓ jaune qu'il est recommandé pour plus de confort, 
                      et un ✓ gris qu'il représente un coût récurrent annuel.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};